import { useState, useEffect, useRef } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { usePageTitle } from "@/hooks/use-page-title";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useIncentiveLedger } from "@/hooks/use-queries";
import { format } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import {
  Users,
  Gift,
  Copy,
  Share2,
  TrendingUp,
  CheckCircle,
  Clock,
  DollarSign,
  UserPlus,
  Award,
  Sparkles,
  Link2,
  MessageCircle,
  Mail,
  Phone,
  Star,
  Trophy,
  Target,
} from "lucide-react";
import { SiWhatsapp } from "react-icons/si";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { PageAbout } from "@/components/page-about";

interface ReferralCode {
  id: string;
  userId: string;
  code: string;
  referrerBonus: number;
  refereeBonus: number;
  timesUsed: number;
  maxUses?: number;
  expiresAt?: string;
  isActive: boolean;
  createdAt: string;
}

interface ReferralTracking {
  id: string;
  referralCodeId: string;
  referrerId: string;
  refereeId: string;
  status: string;
  qualifiedAt?: string;
  bonusPaidAt?: string;
  createdAt: string;
  referee?: {
    firstName?: string;
    lastName?: string;
    email?: string;
  };
}

interface IncentiveLedger {
  id: string;
  userId: string;
  type: string;
  amount: number;
  description: string;
  status: string;
  createdAt: string;
}

function formatCents(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

function getStatusBadge(status: string) {
  switch (status) {
    case "qualified":
      return <Badge className="bg-green-500/10 text-green-600 border-green-500/20"><CheckCircle className="w-3 h-3 mr-1" /> Qualified</Badge>;
    case "pending":
      return <Badge className="bg-yellow-500/10 text-yellow-600 border-yellow-500/20"><Clock className="w-3 h-3 mr-1" /> Pending</Badge>;
    case "paid":
      return <Badge className="bg-blue-500/10 text-blue-600 border-blue-500/20"><DollarSign className="w-3 h-3 mr-1" /> Bonus Paid</Badge>;
    default:
      return <Badge variant="secondary">{status}</Badge>;
  }
}

export default function ReferralsPage() {
  usePageTitle("Referral Program", "Share HealthWallet with your network and earn rewards through the referral program.");
  const { toast } = useToast();
  const { t, i18n } = useTranslation();

  const { data: referralCodes, isLoading: codesLoading } = useQuery<ReferralCode[]>({
    queryKey: ["/api/referral-codes"],
  });

  const activeCode = referralCodes?.find(c => c.isActive !== false);

  const { data: referrals, isLoading: referralsLoading } = useQuery<ReferralTracking[]>({
    queryKey: ["/api/referrals"],
  });

  const { data: incentives, isLoading: incentivesLoading } = useIncentiveLedger();

  const autoGenerated = useRef(false);
  
  const createCodeMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("POST", "/api/referral-codes");
      return response.json() as Promise<ReferralCode>;
    },
    onSuccess: (newCode) => {
      queryClient.setQueryData<ReferralCode[]>(["/api/referral-codes"], (old) => {
        if (!old) return [newCode];
        const without = old.filter(c => c.id !== newCode.id);
        return [newCode, ...without];
      });
      queryClient.invalidateQueries({ queryKey: ["/api/referral-codes"] });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create referral code",
        variant: "destructive",
      });
    },
  });

  useEffect(() => {
    if (!codesLoading && referralCodes && !activeCode && !autoGenerated.current && !createCodeMutation.isPending) {
      autoGenerated.current = true;
      createCodeMutation.mutate();
    }
  }, [codesLoading, referralCodes, activeCode]);

  const getReferralLink = () => activeCode ? `${window.location.origin}/?ref=${activeCode.code}` : "";

  const shareMessages: Record<string, { subject: string; body: string; short: string }> = {
    en: {
      subject: "Join HealthWallet - Get a $10 Welcome Bonus",
      body: "Join HealthWallet and get a $10 welcome bonus for your healthcare fund! Pre-fund your medical expenses with community support.",
      short: "Pre-fund your healthcare with community support. Get a $10 welcome bonus!",
    },
    es: {
      subject: "Unete a HealthWallet - Recibe un bono de bienvenida de $10",
      body: "Unete a HealthWallet y recibe un bono de bienvenida de $10 para tu fondo de salud. Pre-financia tus gastos medicos con apoyo comunitario.",
      short: "Pre-financia tu salud con apoyo comunitario. Recibe un bono de $10!",
    },
    so: {
      subject: "Ku biir HealthWallet - Hel bonus soo dhaweyn $10",
      body: "Ku biir HealthWallet oo hel bonus soo dhaweyn $10 sanduuqaaga caafimaadka! Hore u maalgeliso kharashkaaga caafimaadka taageerada bulshada.",
      short: "Hore u maalgeliso caafimaadkaaga taageerada bulshada. Hel bonus $10!",
    },
    hmn: {
      subject: "Koom nrog HealthWallet - Tau txais $10 nyiaj txais tos",
      body: "Koom nrog HealthWallet thiab tau txais $10 nyiaj txais tos rau koj lub hnab kho mob! Ua ntej them koj cov nqi kho mob nrog kev pab los ntawm zej zog.",
      short: "Ua ntej them koj cov nqi kho mob nrog kev pab zej zog. Tau $10!",
    },
  };

  const currentLang = i18n.language?.split("-")[0] || "en";
  const messages = shareMessages[currentLang] || shareMessages.en;

  const getShareMessage = () => {
    const link = getReferralLink();
    return `${messages.body} Sign up here: ${link}`;
  };

  const copyReferralLink = () => {
    if (activeCode) {
      navigator.clipboard.writeText(getReferralLink());
      toast({
        title: "Link Copied!",
        description: "Share this link with friends and family to earn bonuses.",
      });
    }
  };

  const copyReferralCode = () => {
    if (activeCode) {
      navigator.clipboard.writeText(activeCode.code);
      toast({
        title: "Code Copied!",
        description: `Your referral code: ${activeCode.code}`,
      });
    }
  };

  const shareViaWhatsApp = () => {
    const message = encodeURIComponent(getShareMessage());
    window.open(`https://wa.me/?text=${message}`, "_blank");
  };

  const shareViaSMS = () => {
    const message = encodeURIComponent(getShareMessage());
    window.open(`sms:?body=${message}`, "_blank");
  };

  const shareViaEmail = () => {
    const subject = encodeURIComponent(messages.subject);
    const body = encodeURIComponent(getShareMessage());
    window.open(`mailto:?subject=${subject}&body=${body}`, "_blank");
  };

  const shareNative = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "HealthWallet",
          text: messages.short,
          url: getReferralLink(),
        });
      } catch (err) {
        copyReferralLink();
      }
    } else {
      copyReferralLink();
    }
  };

  const milestones = [
    { name: "Starter", threshold: 1, bonus: 2500, icon: Star },
    { name: "Silver", threshold: 3, bonus: 5000, icon: Award },
    { name: "Gold", threshold: 5, bonus: 7500, icon: Trophy },
    { name: "Platinum", threshold: 10, bonus: 10000, icon: Target },
  ];

  const totalReferrals = (referrals?.length || 0);
  const currentTier = [...milestones].reverse().find(m => totalReferrals >= m.threshold) || null;
  const nextTier = milestones.find(m => totalReferrals < m.threshold) || null;
  const tierProgress = nextTier ? (totalReferrals / nextTier.threshold) * 100 : 100;

  const totalEarned = incentives?.filter(i => i.type === "referral_bonus" && i.status === "credited")
    .reduce((sum, i) => sum + i.amount, 0) || 0;

  const pendingEarnings = incentives?.filter(i => i.type === "referral_bonus" && i.status === "pending")
    .reduce((sum, i) => sum + i.amount, 0) || 0;

  const qualifiedReferrals = referrals?.filter(r => r.status === "qualified" || r.status === "paid").length || 0;
  const pendingReferrals = referrals?.filter(r => r.status === "pending").length || 0;

  if (codesLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid gap-6 md:grid-cols-4">
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageAbout
        title="Referral Center"
        description="Earn wallet credits by inviting friends and family to join HealthWallet. Both you and your referrals receive bonuses when they sign up and become active users."
        features={[
          "Unique referral code and shareable link for each user",
          "Earn bonus credits when your referrals qualify",
          "Track pending and qualified referrals in real-time",
          "View your complete referral bonus history",
          "No limit on how many people you can refer"
        ]}
        tips={[
          "Share your referral link on social media to reach more people",
          "Referrals qualify once they complete their first contribution",
          "Check the pending tab to see who's close to qualifying"
        ]}
      />
      
      <div className="flex items-center justify-between gap-2">
        <div>
          <h1 className="text-3xl font-semibold" data-testid="text-page-title">Referral Center</h1>
          <p className="text-muted-foreground mt-1">
            Invite friends and earn wallet credits for each successful referral
          </p>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 gap-2">
            <CardTitle className="text-sm font-medium">Total Earned</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{formatCents(totalEarned)}</div>
            <p className="text-xs text-muted-foreground">From referral bonuses</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 gap-2">
            <CardTitle className="text-sm font-medium">Pending</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">{formatCents(pendingEarnings)}</div>
            <p className="text-xs text-muted-foreground">Awaiting qualification</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 gap-2">
            <CardTitle className="text-sm font-medium">Qualified Referrals</CardTitle>
            <CheckCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{qualifiedReferrals}</div>
            <p className="text-xs text-muted-foreground">Successful referrals</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 gap-2">
            <CardTitle className="text-sm font-medium">Pending Referrals</CardTitle>
            <UserPlus className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{pendingReferrals}</div>
            <p className="text-xs text-muted-foreground">Awaiting actions</p>
          </CardContent>
        </Card>
      </div>

      <Card className="border-2 border-primary/20 bg-primary/5">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Gift className="w-5 h-5 text-primary" />
            Your Referral Code
          </CardTitle>
          <CardDescription>
            Share your unique code with friends. You both earn rewards!
          </CardDescription>
        </CardHeader>
        <CardContent>
          {activeCode ? (
            <div className="space-y-4">
              <div className="flex items-center gap-4 flex-wrap">
                <div className="flex-1 min-w-[200px]">
                  <Input
                    value={activeCode.code}
                    readOnly
                    className="font-mono text-lg text-center bg-background"
                    data-testid="input-referral-code"
                  />
                </div>
                <Button variant="outline" onClick={copyReferralCode} data-testid="button-copy-code">
                  <Copy className="w-4 h-4 mr-2" />
                  Copy Code
                </Button>
                <Button onClick={copyReferralLink} data-testid="button-copy-link">
                  <Link2 className="w-4 h-4 mr-2" />
                  Copy Link
                </Button>
              </div>
              
              <Separator />
              
              <div>
                <p className="text-sm font-medium mb-3">Share with friends and family</p>
                <div className="flex items-center gap-2 flex-wrap">
                  <Button variant="outline" onClick={shareViaWhatsApp} data-testid="button-share-whatsapp" className="gap-2">
                    <SiWhatsapp className="w-4 h-4" />
                    WhatsApp
                  </Button>
                  <Button variant="outline" onClick={shareViaSMS} data-testid="button-share-sms" className="gap-2">
                    <Phone className="w-4 h-4" />
                    Text Message
                  </Button>
                  <Button variant="outline" onClick={shareViaEmail} data-testid="button-share-email" className="gap-2">
                    <Mail className="w-4 h-4" />
                    Email
                  </Button>
                  <Button variant="outline" onClick={shareNative} data-testid="button-share-native" className="gap-2">
                    <Share2 className="w-4 h-4" />
                    More
                  </Button>
                </div>
              </div>

              <Separator />
              
              <div className="grid gap-4 md:grid-cols-3">
                <div className="p-4 rounded-lg bg-background border">
                  <div className="flex items-center gap-2 mb-1">
                    <Gift className="w-4 h-4 text-green-600" />
                    <span className="text-sm font-medium">You Earn</span>
                  </div>
                  <div className="text-xl font-bold text-green-600">
                    {formatCents(activeCode.referrerBonus || 2500)}
                  </div>
                  <p className="text-xs text-muted-foreground">Per qualified referral</p>
                </div>
                
                <div className="p-4 rounded-lg bg-background border">
                  <div className="flex items-center gap-2 mb-1">
                    <Sparkles className="w-4 h-4 text-blue-600" />
                    <span className="text-sm font-medium">Friend Gets</span>
                  </div>
                  <div className="text-xl font-bold text-blue-600">
                    {formatCents(activeCode.refereeBonus || 1000)}
                  </div>
                  <p className="text-xs text-muted-foreground">Welcome bonus</p>
                </div>
                
                <div className="p-4 rounded-lg bg-background border">
                  <div className="flex items-center gap-2 mb-1">
                    <Users className="w-4 h-4 text-purple-600" />
                    <span className="text-sm font-medium">Invites Sent</span>
                  </div>
                  <div className="text-xl font-bold">
                    {activeCode.timesUsed || 0}
                    {activeCode.maxUses && <span className="text-muted-foreground">/{activeCode.maxUses}</span>}
                  </div>
                  <p className="text-xs text-muted-foreground">Times used</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-6">
              <Gift className="w-12 h-12 mx-auto mb-4 text-muted-foreground animate-pulse" />
              <p className="text-muted-foreground mb-4">Generating your referral code...</p>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trophy className="w-5 h-5 text-primary" />
            Referral Milestones
          </CardTitle>
          <CardDescription>
            Earn bigger bonuses as you refer more people
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {nextTier && (
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-2 text-sm">
                  <span className="text-muted-foreground">
                    {totalReferrals} of {nextTier.threshold} referrals to reach {nextTier.name}
                  </span>
                  <span className="font-medium">{Math.round(tierProgress)}%</span>
                </div>
                <Progress value={tierProgress} className="h-2" data-testid="progress-milestone" />
              </div>
            )}
            {!nextTier && totalReferrals > 0 && (
              <div className="flex items-center gap-2 text-sm text-green-600">
                <CheckCircle className="w-4 h-4" />
                <span className="font-medium">You've reached the highest tier!</span>
              </div>
            )}
            <div className="grid gap-3 md:grid-cols-4">
              {milestones.map((milestone) => {
                const MilestoneIcon = milestone.icon;
                const isReached = totalReferrals >= milestone.threshold;
                const isCurrent = currentTier?.name === milestone.name;
                return (
                  <div
                    key={milestone.name}
                    className={`p-4 rounded-lg border text-center transition-colors ${
                      isCurrent
                        ? "border-primary bg-primary/5"
                        : isReached
                        ? "border-green-500/30 bg-green-500/5"
                        : "opacity-60"
                    }`}
                    data-testid={`milestone-${milestone.name.toLowerCase()}`}
                  >
                    <MilestoneIcon className={`w-6 h-6 mx-auto mb-2 ${
                      isCurrent ? "text-primary" : isReached ? "text-green-600" : "text-muted-foreground"
                    }`} />
                    <p className="font-medium text-sm">{milestone.name}</p>
                    <p className="text-xs text-muted-foreground">{milestone.threshold} referrals</p>
                    <p className="text-sm font-bold mt-1 text-green-600">{formatCents(milestone.bonus)}</p>
                    <p className="text-xs text-muted-foreground">bonus each</p>
                    {isReached && (
                      <Badge variant="secondary" className="mt-2 text-xs">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Reached
                      </Badge>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="referrals">
        <TabsList>
          <TabsTrigger value="referrals" data-testid="tab-referrals">Referrals</TabsTrigger>
          <TabsTrigger value="earnings" data-testid="tab-earnings">Earnings History</TabsTrigger>
        </TabsList>
        
        <TabsContent value="referrals" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Your Referrals</CardTitle>
              <CardDescription>Track who you've invited and their status</CardDescription>
            </CardHeader>
            <CardContent>
              {referralsLoading ? (
                <div className="space-y-2">
                  <Skeleton className="h-12 w-full" />
                  <Skeleton className="h-12 w-full" />
                  <Skeleton className="h-12 w-full" />
                </div>
              ) : referrals && referrals.length > 0 ? (
                <div className="space-y-2">
                  {referrals.map((referral) => (
                    <div
                      key={referral.id}
                      className="flex items-center justify-between gap-2 p-4 rounded-lg border"
                      data-testid={`referral-row-${referral.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-full bg-primary/10">
                          <UserPlus className="w-4 h-4 text-primary" />
                        </div>
                        <div>
                          <p className="font-medium">
                            {referral.referee?.firstName 
                              ? `${referral.referee.firstName} ${referral.referee.lastName || ""}`
                              : "Invited User"
                            }
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Joined {format(new Date(referral.createdAt), "MMM d, yyyy")}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        {referral.qualifiedAt && (
                          <span className="text-xs text-muted-foreground">
                            Qualified {format(new Date(referral.qualifiedAt), "MMM d")}
                          </span>
                        )}
                        {getStatusBadge(referral.status)}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No referrals yet</p>
                  <p className="text-sm">Share your code to start earning!</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="earnings" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Earnings History</CardTitle>
              <CardDescription>All your referral bonuses and rewards</CardDescription>
            </CardHeader>
            <CardContent>
              {incentivesLoading ? (
                <div className="space-y-2">
                  <Skeleton className="h-12 w-full" />
                  <Skeleton className="h-12 w-full" />
                </div>
              ) : incentives && incentives.length > 0 ? (
                <div className="space-y-2">
                  {incentives.map((incentive) => (
                    <div
                      key={incentive.id}
                      className="flex items-center justify-between gap-2 p-4 rounded-lg border"
                      data-testid={`incentive-row-${incentive.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-full ${
                          incentive.status === "credited" 
                            ? "bg-green-500/10" 
                            : "bg-yellow-500/10"
                        }`}>
                          {incentive.status === "credited" ? (
                            <CheckCircle className="w-4 h-4 text-green-600" />
                          ) : (
                            <Clock className="w-4 h-4 text-yellow-600" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium">{incentive.description}</p>
                          <p className="text-xs text-muted-foreground">
                            {format(new Date(incentive.createdAt), "MMM d, yyyy h:mm a")}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-bold text-green-600">
                          +{formatCents(incentive.amount)}
                        </div>
                        <Badge variant={incentive.status === "credited" ? "default" : "secondary"} className="text-xs">
                          {incentive.status}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Award className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No earnings yet</p>
                  <p className="text-sm">Start referring to earn bonuses!</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            How It Works
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-6 md:grid-cols-3">
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-bold">
                1
              </div>
              <div>
                <h4 className="font-medium">Share Your Code</h4>
                <p className="text-sm text-muted-foreground">
                  Send your unique referral code or link to friends and family
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-bold">
                2
              </div>
              <div>
                <h4 className="font-medium">They Sign Up</h4>
                <p className="text-sm text-muted-foreground">
                  Your friend creates an account and completes their first action
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-bold">
                3
              </div>
              <div>
                <h4 className="font-medium">You Both Earn</h4>
                <p className="text-sm text-muted-foreground">
                  Both of you receive bonus credits in your HealthWallets
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
